/**
 * A switch — the accessible on/off control.
 *
 * The repo had no toggle, so this is the first one. It is a real
 * `role="switch"` button rather than a styled checkbox, because the
 * notification channels need to carry a *pending* state (the browser is
 * asking for permission) that a checkbox cannot express.
 */

import { cn } from '@/lib/utils'

export interface SwitchProps {
  checked: boolean
  onChange: (next: boolean) => void
  disabled?: boolean
  /** Announced to screen readers, e.g. "Browser notifications". */
  label: string
  className?: string
}

export function Switch({ checked, onChange, disabled, label, className }: SwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative inline-flex h-6 w-11 shrink-0 items-center border-[3px] border-[rgb(var(--border))]',
        'bg-[rgb(var(--bg-subtle))] transition-colors',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[rgb(var(--accent))]',
        'disabled:cursor-not-allowed disabled:opacity-50',
        checked && 'bg-[rgb(var(--accent))]',
        className,
      )}
    >
      <span
        aria-hidden
        className={cn(
          'block h-3.5 w-3.5 bg-[rgb(var(--bg-elevated))] transition-transform',
          checked ? 'translate-x-[1.35rem]' : 'translate-x-[0.15rem]',
        )}
      />
    </button>
  )
}
